import React, { useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LinkIcon from "@mui/icons-material/Link";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { getYoutubeId, getVimeoId } from "./videoMeta";

/*  Upload status helpers  */

const UPLOAD_STATUS_CONFIG = {
  pending: {
    label: "Upload Queued",
    color: "warning",
    icon: <HourglassEmptyIcon fontSize="small" />,
    canPlay: false,
    message: "Video is queued for upload. Please check back shortly.",
  },
  processing: {
    label: "Processing…",
    color: "info",
    icon: <SyncIcon fontSize="small" sx={{ animation: "spin 1.2s linear infinite", "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />,
    canPlay: false,
    message: "Video is being uploaded to the media server. Please wait.",
  },
  completed: {
    label: "Ready",
    color: "success",
    icon: <PlayArrowIcon fontSize="small" />,
    canPlay: true,
    message: null,
  },
  failed: {
    label: "Upload Failed",
    color: "error",
    icon: <ErrorOutlineIcon fontSize="small" />,
    canPlay: false,
    message: "Video upload failed. Please delete and re-upload the tutorial.",
  },
};

const getUploadStatusConfig = (status) =>
  UPLOAD_STATUS_CONFIG[status] || UPLOAD_STATUS_CONFIG.completed;

/*  Video player renderer ── */

const VideoPlayer = ({ videoUrl, title }) => {
  if (!videoUrl) return null;

  const ytId = getYoutubeId(videoUrl);
  if (ytId) {
    return (
      <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <iframe
          title={title}
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Box>
    );
  }

  const vimeoId = getVimeoId(videoUrl);
  if (vimeoId) {
    return (
      <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <iframe
          title={title}
          src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </Box>
    );
  }

  // Direct video file (media server URL)
  return (
    <Box sx={{ width: "100%", bgcolor: "#000", borderRadius: 1, overflow: "hidden" }}>
      <video
        controls
        autoPlay
        style={{ width: "100%", maxHeight: "65vh", display: "block" }}
        src={videoUrl}
      >
        Your browser does not support the video tag.
      </video>
    </Box>
  );
};

/*  Main Component ─ */

/**
 * TutorialPlayerModal
 * Admin preview modal for knowledge base tutorials.
 *
 * Rules:
 * - uploadStatus !== "completed" → play button blocked, message shown
 * - uploadStatus === "completed" → renders video player (iframe for YT/Vimeo, <video> for file)
 */
const TutorialPlayerModal = ({ open, onClose, tutorial }) => {
  if (!tutorial) return null;

  const statusConfig = getUploadStatusConfig(tutorial.uploadStatus || "completed");
  const canPlay = tutorial.videoSourceType === "url" || statusConfig.canPlay;

  let errorDetails = null;
  if (tutorial.uploadStatus === "failed" && tutorial.uploadError) {
    try {
      const parsed = typeof tutorial.uploadError === "string"
        ? JSON.parse(tutorial.uploadError)
        : tutorial.uploadError;
      errorDetails = parsed?.message || String(tutorial.uploadError);
    } catch (_) {
      errorDetails = String(tutorial.uploadError);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#111827",
          color: "#f9fafb",
        },
      }}
    >
      {/* ── Header ─ */}
      <DialogTitle
        sx={{
          bgcolor: "#1f2937",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          {tutorial.videoSourceType === "url" ? (
            <LinkIcon sx={{ color: "#60a5fa", flexShrink: 0 }} />
          ) : (
            <VideoFileIcon sx={{ color: "#a78bfa", flexShrink: 0 }} />
          )}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: "#f9fafb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {tutorial.title}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {/* Upload status badge */}
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.72rem" }}
          />
          <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af", ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Body  */}
      <DialogContent sx={{ p: 0, bgcolor: "#111827" }}>
        {/* Processing progress bar */}
        {tutorial.uploadStatus === "processing" && (
          <LinearProgress color="info" sx={{ height: 3 }} />
        )}

        {canPlay ? (
          <Box sx={{ p: 1 }}>
            <VideoPlayer videoUrl={tutorial.videoUrl} title={tutorial.title} />
          </Box>
        ) : (
          /* Blocked state — non-completed upload */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 280,
              p: 4,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: tutorial.uploadStatus === "failed" ? "#fee2e2" : "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {tutorial.uploadStatus === "processing" ? (
                <CircularProgress size={36} color="info" />
              ) : tutorial.uploadStatus === "failed" ? (
                <ErrorOutlineIcon sx={{ fontSize: 36, color: "#ef4444" }} />
              ) : (
                <HourglassEmptyIcon sx={{ fontSize: 36, color: "#f59e0b" }} />
              )}
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#f9fafb", textAlign: "center" }}>
              {statusConfig.label}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af", textAlign: "center", maxWidth: 360 }}>
              {statusConfig.message}
            </Typography>
            {errorDetails && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "#2d1515",
                  borderRadius: 1,
                  border: "1px solid #7f1d1d",
                  maxWidth: "100%",
                  overflow: "auto",
                }}
              >
                <Typography variant="caption" sx={{ color: "#fca5a5", fontFamily: "monospace" }}>
                  {errorDetails}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Meta info row */}
        {canPlay && (tutorial.duration || tutorial.moduleName) && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "#1f2937",
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {tutorial.moduleName && (
              <Chip label={tutorial.moduleName} size="small" sx={{ bgcolor: "#374151", color: "#d1d5db", fontSize: "0.72rem" }} />
            )}
            {tutorial.duration && (
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                ⏱ {tutorial.duration}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ─ */}
      <DialogActions sx={{ bgcolor: "#1f2937", px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ color: "#9ca3af", borderColor: "#374151" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialPlayerModal;
